import { Terminal } from '@/components/terminal/Terminal';
import { TerminalPhase } from '@/components/terminal/types';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { usePresentationContext } from './PresentationContext';
import { VideoBackground } from './VideoBackground';

interface SlideProps {
  children: ReactNode;
  className?: string;
  notes?: string;
  transition?: 'none' | 'fade' | 'slide' | 'convex' | 'concave' | 'zoom';
  background?: {
    color?: string;
    image?: string;
    video?: string;
  };
  audio?: string;
  visualizer?: 'full' | 'bottom-right' | 'hidden';
  terminal?: {
    phases: TerminalPhase[];
    config?: {
      title?: string;
      typingSpeed?: number;
      phaseDelay?: number;
    };
  };
  autoNext?: number;
  networkBackground?: 'full' | 'hidden';
}

export const Slide = ({
  children,
  className = '',
  notes,
  transition,
  background,
  audio,
  visualizer,
  terminal,
  autoNext,
  networkBackground = 'hidden',
}: SlideProps) => {
  const { deck, isInitialized } = usePresentationContext();
  const [isActive, setIsActive] = useState(false);
  const [isNearby, setIsNearby] = useState(false);
  const slideRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!deck || !isInitialized || !slideRef.current) return;

    const checkSlideState = () => {
      const currentSlide = deck.getCurrentSlide();
      const thisSlide = slideRef.current;
      if (!thisSlide) return;

      const allSlides = deck.getSlides();

      // Check if this slide is the current one
      const isCurrent = currentSlide === thisSlide;
      setIsActive(isCurrent);

      // Find indices to determine if nearby
      const currentIndex = allSlides.indexOf(currentSlide);
      const thisIndex = allSlides.indexOf(thisSlide);

      if (currentIndex !== -1 && thisIndex !== -1) {
        const distance = Math.abs(thisIndex - currentIndex);
        setIsNearby(distance <= 1);
      } else {
        setIsNearby(false);
      }
    };

    const handleSlideChange = () => {
      checkSlideState();
    };

    deck.on('slidechanged', handleSlideChange);

    // Check initial state
    checkSlideState();

    return () => {
      deck.off('slidechanged', handleSlideChange);
    };
  }, [deck, isInitialized]);

  const dataAttributes: Record<string, string> = {
    ...(notes && { 'data-notes': notes }),
    ...(transition && { 'data-transition': transition }),
    ...(background?.color && { 'data-background-color': background.color }),
    ...(background?.image && { 'data-background-image': background.image }),
    // Removed video attributes - handled by VideoBackground component
    ...(audio && { 'data-audio': audio }),
    ...(visualizer && { 'data-visualizer': visualizer }),
    'data-has-terminal': terminal ? 'true' : 'false',
    ...(autoNext && { 'data-auto-next': autoNext.toString() }),
    'data-network-background': networkBackground,
    'data-active': isActive.toString(),
  };

  return (
    <section ref={slideRef} {...dataAttributes} className={`slide ${className} relative`}>
      {background?.video && (
        <VideoBackground src={background.video} isActive={isActive} isNearby={isNearby} />
      )}
      {children}
      {terminal && <Terminal {...terminal} className="absolute inset-0 z-10" />}
      {notes && <aside className="notes">{notes}</aside>}
    </section>
  );
};
